import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { TextFieldElement, PasswordElement, useForm } from 'react-hook-form-mui'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Box, Link, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import Title from './Title'
import usePageErrorMessages from '../hooks/usePageErrorMessages'
import useHiddenFormElement from '../hooks/useHiddenFormElement'
import useFormAnalytics from '../hooks/useFormAnalytics'
import FormContainer from './FormContainer'

interface Fields {
  username: string
  password: string
}

const SignInForm: FC = () => {
  const [forgottenPasswordLink] = useState(document.querySelector<HTMLLinkElement>('#forgotPassword')?.href)
  const [createAccountLink] = useState(document.querySelector<HTMLLinkElement>('#createAccount')?.href)

  const signInSchema = z.object({
    username: z.string().trim().email('Please enter a valid email address'),
    password: z.string().nonempty('Please enter your password'),
  })

  const [initialEmailInputValue] = useState(document.querySelector<HTMLInputElement>('#signInName')?.value)
  const formContext = useForm<Fields>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      username: initialEmailInputValue,
    },
  })

  const analytics = useFormAnalytics('sign_in')

  const onSubmit = useCallback(
    ({ username, password }: Fields) => {
      document.querySelector<HTMLInputElement>('#signInName')!.value = username
      document.querySelector<HTMLInputElement>('#password')!.value = password
      analytics.onSubmit()
      document.querySelector<HTMLButtonElement>('#next')!.click()
    },
    [analytics],
  )

  const pageErrorMessages = usePageErrorMessages()

  const workingElement = useHiddenFormElement('.working')
  const isWorking = useMemo(() => workingElement?.getAttribute('style')?.includes('block') ?? false, [workingElement])
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  useEffect(() => {
    setIsLoggingIn((_isLoggingIn) => isWorking || (pageErrorMessages.length === 0 && _isLoggingIn))
  }, [pageErrorMessages, isWorking])

  // Unfortunately the code below does not work reliably
  // Different browser work in different ways, and if the beforeunload event is not supported
  // the outcome is catastrophic... the user cannot log in
  // This is too risky to leave in unless we have really solid testing and know which browsers support it
  //
  // // This can happen in edge cases where the state for the auth flow has already been used
  // // It is a strange edge case created by:
  // // 1. User clicks login from two tabs in the same app
  // // 2. User then continues logging in in tab they first opened
  // // 3. This 'steals' the state from the second tab
  // // 4. Second tab can no longer continue and just result in no navigation after clicking the login button
  // // - note this is only a problem in web browsers, and this fix causes issues for native clients, hence the final condition
  // // - we debounce the result as the error message timing is not consistent
  // const isNavigatingAway = useIsNavigatingAway()
  // const [isNotNavigatingError] = useDebounce(
  //   isLoggingIn && !isWorking && !isNavigatingAway && pageErrorMessages.length === 0 && !isNativePolicy,
  //   250,
  // )
  // const cancelButtonAction = useCancelButtonAction()
  // useEffect(() => {
  //   isNotNavigatingError && cancelButtonAction()
  // })

  return (
    <FormContainer formContext={formContext} onSuccess={onSubmit} onError={analytics.onError} onBlur={analytics.onBlur}>
      <Stack spacing={6}>
        <Title>Sign in</Title>
        <Box>
          <TextFieldElement
            type="email"
            name="username"
            label="Email Address"
            autoComplete="username"
            autoFocus
            required
            fullWidth
            disabled={isLoggingIn}
          />
          <PasswordElement
            name="password"
            label="Password"
            autoComplete="current-password"
            required
            fullWidth
            disabled={isLoggingIn}
          />
        </Box>
        <Link
          sx={{ width: 'fit-content' }}
          href={forgottenPasswordLink}
          onClick={() => analytics.onOther('forgotten_password_click')}
        >
          Forgot your password
        </Link>
        <Box>
          {pageErrorMessages && (
            <Typography component="div" mb={2} color="error" dangerouslySetInnerHTML={{ __html: pageErrorMessages }} />
          )}
          <LoadingButton fullWidth type="submit" variant="contained" loading={isLoggingIn}>
            Sign In
          </LoadingButton>
        </Box>
        <Typography>
          Don't have an account?
          <Link sx={{ ml: 1 }} href={createAccountLink} onClick={() => analytics.onOther('sign_up_click')}>
            Sign up now
          </Link>
        </Typography>
      </Stack>
    </FormContainer>
  )
}

export default SignInForm
